import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import {
  candidateQueryKey,
  fetchCandidate,
  upsertCandidate,
} from "@/lib/queries/candidate";
import { supabase } from "@/integrations/supabase/client";
import { reindexCandidateChunks } from "@/server/reindex-candidate";
import { backfillEmbeddings } from "@/server/backfill-embeddings";

// ---------- Types ----------
export type SkillLevel = "beginner" | "intermediate" | "expert";

export type VerifiedSkill = {
  name: string;
  level?: SkillLevel;
  source?: "resume" | "github" | "linkedin" | "portfolio" | string;
};

export type ExperienceItem = {
  company: string;
  role: string;
  start: string; // YYYY-MM
  end: string | null;
  description?: string;
  highlights?: string[];
};

export type EducationItem = {
  school: string;
  degree: string;
  start: string;
  end: string | null;
};

export type ResumeData = {
  rawText: string | null;
  summary: string | null;
  chronologicalExperience: ExperienceItem[];
  education: EducationItem[];
  certifications: string[];
  awards: string[];
  fileName: string | null;
  filePath: string | null;
};

export type RepoItem = {
  name: string;
  description?: string;
  stars?: number;
  language?: string;
  url?: string;
};

export type GitHubData = {
  username: string | null;
  totalRepos: number;
  topLanguages: { name: string; bytes: number }[];
  stars: number;
  followers: number;
  pinnedRepos: RepoItem[];
};

export type SkillEndorsement = { skill: string; count: number };
export type Recommendation = { author: string; relationship: string; text: string };

export type LinkedInData = {
  headline: string | null;
  location: string | null;
  connections: number;
  endorsements: SkillEndorsement[];
  recommendations: Recommendation[];
  profileUrl: string | null;
  rawText: string | null;
  pdfFileName: string | null;
};

export type PortfolioCase = {
  title: string;
  url?: string;
  summary?: string;
  impact?: string;
};

export type PortfolioData = {
  cases: PortfolioCase[];
  links: string[];
};

export type CandidateProfile = {
  verifiedSkills: VerifiedSkill[];
  resume: ResumeData;
  github: GitHubData;
  linkedin: LinkedInData;
  portfolio: PortfolioData;
};

// ---------- Initial state ----------
export const emptyCandidateProfile: CandidateProfile = {
  verifiedSkills: [],
  resume: {
    rawText: null,
    summary: null,
    chronologicalExperience: [],
    education: [],
    certifications: [],
    awards: [],
    fileName: null,
    filePath: null,
  },
  github: {
    username: null,
    totalRepos: 0,
    topLanguages: [],
    stars: 0,
    followers: 0,
    pinnedRepos: [],
  },
  linkedin: {
    headline: null,
    location: null,
    connections: 0,
    endorsements: [],
    recommendations: [],
    profileUrl: null,
    rawText: null,
    pdfFileName: null,
  },
  portfolio: {
    cases: [],
    links: [],
  },
};

// ---------- Context ----------
type ProfilePatch = {
  verifiedSkills?: VerifiedSkill[];
  resume?: Partial<ResumeData>;
  github?: Partial<GitHubData>;
  linkedin?: Partial<LinkedInData>;
  portfolio?: Partial<PortfolioData>;
};

type CandidateCtx = {
  profile: CandidateProfile;
  loading: boolean;
  setVerifiedSkills: (s: VerifiedSkill[]) => void;
  setResume: (r: Partial<ResumeData>) => void;
  setGithub: (g: Partial<GitHubData>) => void;
  setLinkedin: (l: Partial<LinkedInData>) => void;
  setPortfolio: (p: Partial<PortfolioData>) => void;
  /**
   * Apply many partial updates atomically as a SINGLE DB write. Use this
   * whenever multiple sections change together (e.g. onboarding finish,
   * profile sync) — calling several setters in the same tick races and
   * the writes can overwrite each other since profileToRow writes the
   * full row.
   */
  updateProfile: (patch: ProfilePatch) => Promise<void>;
  /** Refetch latest from DB and return it. */
  refetchProfile: () => Promise<CandidateProfile>;
  reset: () => void;
};

const CandidateContext = createContext<CandidateCtx | undefined>(undefined);

export function CandidateProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: userId ? candidateQueryKey(userId) : ["candidate", "anonymous"],
    queryFn: () => fetchCandidate(userId as string),
    enabled: !!userId,
  });

  const profile = data ?? emptyCandidateProfile;

  const mutation = useMutation({
    mutationFn: async (next: CandidateProfile) => {
      if (!userId) return;
      await upsertCandidate(userId, next);
    },
    onMutate: async (next) => {
      if (!userId) return;
      const key = candidateQueryKey(userId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CandidateProfile>(key);
      queryClient.setQueryData(key, next);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (!userId || !ctx) return;
      queryClient.setQueryData(candidateQueryKey(userId), ctx.previous);
    },
    onSettled: () => {
      if (!userId) return;
      queryClient.invalidateQueries({ queryKey: candidateQueryKey(userId) });
      // Schedule a debounced reindex + embedding refresh after profile changes
      scheduleReindex();
    },
  });

  // ---- Auto-reindex on profile change (debounced, server-side) ----
  const reindexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reindexInFlightRef = useRef(false);

  async function getAuthHeaders(): Promise<Record<string, string>> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { authorization: `Bearer ${token}` } : {};
  }

  const scheduleReindex = () => {
    if (!userId) return;
    if (reindexTimerRef.current) clearTimeout(reindexTimerRef.current);
    reindexTimerRef.current = setTimeout(async () => {
      if (reindexInFlightRef.current) return;
      reindexInFlightRef.current = true;
      try {
        const headers = await getAuthHeaders();
        if (!headers.authorization) return;
        // Fire-and-forget; failures are non-fatal for the user
        await Promise.allSettled([
          reindexCandidateChunks({ headers }),
          backfillEmbeddings({ headers }),
        ]);
      } catch {
        // swallow — reindex is best-effort
      } finally {
        reindexInFlightRef.current = false;
      }
    }, 4000); // 4s debounce — give multi-section saves time to settle
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reindexTimerRef.current) clearTimeout(reindexTimerRef.current);
    };
  }, []);

  // Read the freshest profile from the cache at call time, not from the
  // closure. Multiple set* calls fired in the same tick would otherwise each
  // build their patch from the same stale snapshot and clobber each other.
  const getLatest = (): CandidateProfile => {
    if (!userId) return profile;
    return (
      queryClient.getQueryData<CandidateProfile>(candidateQueryKey(userId)) ??
      profile
    );
  };

  const value = useMemo<CandidateCtx>(
    () => ({
      profile,
      loading: isLoading,
      setVerifiedSkills: (s) => {
        const cur = getLatest();
        mutation.mutate({ ...cur, verifiedSkills: s });
      },
      setResume: (r) => {
        const cur = getLatest();
        mutation.mutate({ ...cur, resume: { ...cur.resume, ...r } });
      },
      setGithub: (g) => {
        const cur = getLatest();
        mutation.mutate({ ...cur, github: { ...cur.github, ...g } });
      },
      setLinkedin: (l) => {
        const cur = getLatest();
        mutation.mutate({ ...cur, linkedin: { ...cur.linkedin, ...l } });
      },
      setPortfolio: (p) => {
        const cur = getLatest();
        mutation.mutate({ ...cur, portfolio: { ...cur.portfolio, ...p } });
      },
      updateProfile: async (patch) => {
        // Always refetch fresh from DB first so we don't overwrite fields
        // that were written out-of-band (e.g. AI-parsed resume).
        let base = getLatest();
        if (userId) {
          try {
            const fresh = await queryClient.fetchQuery({
              queryKey: candidateQueryKey(userId),
              queryFn: () => fetchCandidate(userId),
            });
            base = fresh;
          } catch {
            // fall back to cache
          }
        }
        const next: CandidateProfile = {
          ...base,
          verifiedSkills: patch.verifiedSkills ?? base.verifiedSkills,
          resume: patch.resume ? { ...base.resume, ...patch.resume } : base.resume,
          github: patch.github ? { ...base.github, ...patch.github } : base.github,
          linkedin: patch.linkedin
            ? { ...base.linkedin, ...patch.linkedin }
            : base.linkedin,
          portfolio: patch.portfolio
            ? { ...base.portfolio, ...patch.portfolio }
            : base.portfolio,
        };
        await mutation.mutateAsync(next);
      },
      refetchProfile: async () => {
        if (!userId) return profile;
        const fresh = await queryClient.fetchQuery({
          queryKey: candidateQueryKey(userId),
          queryFn: () => fetchCandidate(userId),
        });
        return fresh;
      },
      reset: () => mutation.mutate(emptyCandidateProfile),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, isLoading, mutation, userId],
  );

  return <CandidateContext.Provider value={value}>{children}</CandidateContext.Provider>;
}

export function useCandidate() {
  const ctx = useContext(CandidateContext);
  if (!ctx) throw new Error("useCandidate must be used within a CandidateProvider");
  return ctx;
}
